import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import FrozenFileDefault from './FrozenFileDefault';
import { resubmitAfterEquifaxAccess } from '../../../../actions/applications';
import { asyncActions } from '../../../../actions/general';
import { getRoute, getSession } from '../../../../actions/sessions';
import useSession from '../../../../hooks/useSession';
import useSessionEntity from '../../../../hooks/useSessionEntity';
import { equifaxAllowAccessUrl } from '../../../../lib/constants/SiteVariables';
import { captureCheckoutEvent } from '../../../../lib/utils/checkout_events';
import Loading from '../../../shared/Loading';

export default function EquifaxAllowAccess({ user, allowAccessToken }) {
  const dispatch = useDispatch();
  const [, sessionActions] = useSession();
  const application = useSessionEntity('application');
  const [status, setStatus] = useState(null);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const trackedEventRef = useRef(false);
  const company = useSessionEntity('company');

  const handleResubmit = useCallback(() => {
    if (!application) return;

    setIsResubmitting(true);

    dispatch(
      asyncActions(
        resubmitAfterEquifaxAccess(application),
        getSession(),
        getRoute()
      )
    ).catch((error) => {
      // Error is already handled by asyncActions via handleAsyncError
      // Reset loading state so user can see the error and try again
      console.error('Error resubmitting after Equifax access:', error);
      setIsResubmitting(false);
    });
    // Note: No finally() block - on success, we keep showing loading state
    // until the Pending component's useEffect calls sessionActions.finish()
  }, [application, dispatch]);

  const handleFailure = useCallback(() => {
    sessionActions.finish();
  }, [sessionActions]);

  useEffect(() => {
    if (!allowAccessToken) return;

    if (!trackedEventRef.current && company) {
      captureCheckoutEvent('equifaxallowaccess', { company, user });
      trackedEventRef.current = true;
    }

    const isValidEquifaxOrigin = (origin) => {
      try {
        const { hostname } = new URL(origin);
        return hostname === 'equifax.com' || hostname.endsWith('.equifax.com');
      } catch {
        return false;
      }
    };

    const handleMessage = (event) => {
      if (!isValidEquifaxOrigin(event.origin)) {
        return;
      }
      const messageStatus = event.data?.status?.toLowerCase();
      if (messageStatus === 'success') {
        setStatus('success');
        handleResubmit();
      } else if (messageStatus === 'fail') {
        setStatus('failure');
        handleFailure();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [allowAccessToken, company, handleResubmit, handleFailure]);

  const renderAllowAccess = () => {
    if (allowAccessToken) {
      return (
        <iframe
          className="w-full h-[600px]"
          src={`${equifaxAllowAccessUrl}?jwt=${allowAccessToken}`}
          title="Equifax Allow Access Authorization"
          sandbox="allow-scripts allow-same-origin allow-forms"
        />
      );
    }

    return null;
  };

  const renderEquifaxAccessForm = () => (
    <div>
      <div className="font-semibold sm:text-lg tracking-wide mb-2 sm:mb-4">
        Hi {user?.first_name},
      </div>
      <div className="font-light text-xs sm:text-base tracking-wider leading-5 mb-1.5 sm:mb-3">
        Your credit file is frozen. Please grant access below to continue with
        your application.
      </div>

      {renderAllowAccess()}
    </div>
  );

  if (isResubmitting) {
    return (
      <div>
        <div className="font-light text-xs sm:text-base tracking-wider leading-5 mb-1.5 sm:mb-3 text-green-600">
          Access granted successfully! Processing your application...
        </div>
        <Loading className="h-14 w-14" containerClass="my-14" />
      </div>
    );
  }

  if (status === 'failure') {
    return <FrozenFileDefault user={user} />;
  }

  return renderEquifaxAccessForm();
}
